import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import { useVoidInvoiceMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

const money = (v: number | string | null | undefined) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Props {
  invoice: any | null;
  open: boolean;
  onClose: () => void;
}

const VoidInvoiceDialog: React.FC<Props> = ({ invoice, open, onClose }) => {
  const [voidInvoice, { isLoading }] = useVoidInvoiceMutation();
  const { showMessage, showErrorMessage } = useNotifier();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    if (open) { setReason(""); setErr(""); }
  }, [open]);

  if (!invoice) return null;

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 3) { setErr("Please provide a reason (at least 3 characters)."); return; }
    try {
      const resp: any = await voidInvoice({ id: invoice.id, reason: r }).unwrap();
      if (resp?.status) { showMessage("Invoice voided."); onClose(); }
      else showErrorMessage(resp?.message || "Failed to void invoice.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to void invoice.");
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Void Invoice</Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.25 }}>
            {invoice.invoiceNumber} · {money(invoice.totalAmount)}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isLoading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1.25, borderRadius: "10px", bgcolor: alpha("#f59e0b", 0.08), border: `1px solid ${alpha("#f59e0b", 0.3)}`, mb: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#b45309" }}>
            Voiding preserves invoice history and receipts but blocks new payments and removes the invoice from active totals.
          </Typography>
        </Box>
        <TextField
          fullWidth
          autoFocus
          multiline
          rows={3}
          label="Reason for voiding"
          required
          value={reason}
          onChange={(e) => { setReason(e.target.value); if (err) setErr(""); }}
          error={!!err}
          helperText={err || " "}
          size="small"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">Cancel</Button>
        <Button
          onClick={submit}
          variant="contained"
          color="warning"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
        >
          {isLoading ? "Voiding…" : "Void Invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidInvoiceDialog;
